import React from 'react';
import {useShallow} from 'zustand/react/shallow';

import {Banner, Icon, Text} from '@albert-io/atomic';
import type {BannerType} from '@albert-io/atomic/molecules/Banner/Banner.react';
import {GuessModelV1} from '@albert-io/models';
import {isGuessOrDraftGuessEmpty} from 'client/AssignmentPracticeView/assignmentPracticeView.utils';

import PartialCreditIcon from 'client/components/PartialCreditIcon/PartialCreditIcon';

import {useQuestionTranslationStore} from '../TranslationSection';

import {guessCorrectnessBanner} from '../TranslationSection/TranslationText';

export type GuessCorrectness = 'unsubmitted' | 'correct' | 'incorrect' | 'partial';

interface Props {
  guess: GuessModelV1;
}

const guessCorrectnessToBannerType = new Map<GuessCorrectness, BannerType>([
  ['unsubmitted', 'neutral'],
  ['incorrect', 'negative'],
  ['partial', 'positive'],
  ['correct', 'positive']
]);

const guessCorrectnessToEnglishCopy = new Map<GuessCorrectness, string>([
  ['unsubmitted', 'No answer was submitted for this question.'],
  ['incorrect', 'Incorrect'],
  ['partial', "You've received partial credit"],
  ['correct', 'Correct']
]);

export default function GuessCorrectnessBanner({guess}: Props) {
  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  let guessCorrectness: GuessCorrectness = 'incorrect';

  let grade: number = guess.get('grade');

  // Sometimes grade can be null or an empty symbol, so we account for that here.
  if (typeof grade !== 'number') {
    grade = 0;
  }

  if (grade > 0 && grade < 1) {
    guessCorrectness = 'partial';
  } else if (grade === 1) {
    guessCorrectness = 'correct';
  } else if (isGuessOrDraftGuessEmpty(guess)) {
    guessCorrectness = 'unsubmitted';
  }

  const copyMap = showTranslations ? guessCorrectnessBanner : guessCorrectnessToEnglishCopy;
  const bannerCopy = copyMap.get(guessCorrectness);
  const bannerType = guessCorrectnessToBannerType.get(guessCorrectness);

  const formattedGrade = grade.toLocaleString(undefined, {style: 'percent'});

  return (
    <Banner className='u-mar-b_4' type={bannerType}>
      <div className='u-display_flex u-justify-content_space-between u-width_100pc'>
        <div className='u-align-items_center u-display_flex u-gap_space-x2'>
          {['correct', 'incorrect'].includes(guessCorrectness) && (
            <Icon icon={guessCorrectness === 'correct' ? 'circle-check' : 'circle-xmark'} />
          )}
          {guessCorrectness === 'partial' && <PartialCreditIcon />}
          <Text bold color='inherit' size='m'>
            {bannerCopy}
          </Text>
        </div>
        {guessCorrectness === 'partial' && (
          <Text color='inherit' size='m'>
            {formattedGrade} {copyMap.get('correct')}
          </Text>
        )}
      </div>
    </Banner>
  );
}
